import { useEffect, type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { fetchMe } from '../lib/api';
import { useAuthStore, type UserRole } from '../lib/auth';

interface RequireAuthProps {
  readonly children: ReactNode;
  readonly requireRole?: readonly UserRole[];
}

export function RequireAuth({ children, requireRole }: RequireAuthProps): JSX.Element {
  const location = useLocation();
  const isHydrated = useAuthStore((s) => s.isHydrated);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const user = useAuthStore((s) => s.user);
  const setAuth = useAuthStore((s) => s.setAuth);
  const markHydrated = useAuthStore((s) => s.markHydrated);

  useEffect(() => {
    if (isHydrated) {
      return;
    }
    let cancelled = false;
    void (async () => {
      const result = await fetchMe();
      if (cancelled) {
        return;
      }
      if (result === null) {
        markHydrated();
      } else {
        setAuth(result.user, result.csrfToken, [...result.permissions]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isHydrated, setAuth, markHydrated]);

  if (!isHydrated) {
    return (
      <div className="loading-state">
        <p>Checking session…</p>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  if (requireRole !== undefined && user !== null && !requireRole.includes(user.role)) {
    return (
      <div className="error-state">
        <p>Access denied for your role ({user.role}).</p>
        <Navigate to="/agents" replace />
      </div>
    );
  }

  return <>{children}</>;
}
